import React, { useRef, useState, useEffect } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { X, UploadCloud, Loader2, Lock, Plus, Type } from "lucide-react";
import api, { formatApiErrorDetail, CATEGORY_DESC, waitForServerUp } from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { useAuth } from "../context/AuthContext";
import { buf2b64, b642buf, MEDIA_CHUNK_MAGIC } from "../lib/crypto";
import { isVideoFile } from "../lib/mediaUpload";
import PostDesigner from "../components/PostDesigner";
import { DomainPicker } from "../components/DomainPicker";
import { CoachTips } from "../components/CoachTip";

const MAX_SLIDES = 10;
const MAX_VIDEO_SECONDS = 900; // 15 minutes
const MAX_VIDEO_BYTES = 500 * 1024 * 1024; // 500 MB
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB upload chunks
const CHUNK_THRESHOLD = 8 * 1024 * 1024; // chunk anything larger than 8 MB

// Read a video file's duration (seconds) from its metadata. Never hangs.
function getVideoDuration(file) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (val, v) => { if (done) return; done = true; try { if (v) URL.revokeObjectURL(v.src); } catch {} resolve(val); };
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => finish(v.duration || 0, v);
      v.onerror = () => finish(0, v);
      v.src = URL.createObjectURL(file);
      setTimeout(() => finish(0, v), 8000); // some mobile browsers never fire the event
    } catch { resolve(0); }
  });
}

// Downscale large images before encrypting/uploading. Encrypted media can't be
// thumbnailed server-side, so smaller originals keep the whole app fast.
async function compressImage(file) {
  if (!file.type.startsWith("image")) return file;
  try {
    const img = await createImageBitmap(file);
    const max = 1280;
    const longest = Math.max(img.width, img.height);
    if (longest <= max && file.size < 900000) return file;
    const scale = Math.min(1, max / longest);
    const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.78));
    return blob && blob.size < file.size ? blob : file;
  } catch { return file; }
}

// Capture the first frame of a video as a bitmap (for the blur-up thumbnail).
// Strictly best-effort: never hangs, resolves null on any timeout/failure so a
// stubborn reel can never freeze the post.
function videoFrameBitmap(file) {
  return new Promise((resolve) => {
    let done = false, vref = null;
    const finish = (val) => { if (done) return; done = true; try { if (vref) URL.revokeObjectURL(vref.src); } catch {} resolve(val); };
    try {
      const v = document.createElement("video");
      vref = v;
      v.muted = true; v.playsInline = true; v.preload = "metadata";
      v.onloadeddata = () => { try { v.currentTime = Math.min(0.1, v.duration || 0); } catch { finish(null); } };
      v.onseeked = async () => {
        try {
          const c = document.createElement("canvas");
          c.width = v.videoWidth; c.height = v.videoHeight;
          c.getContext("2d").drawImage(v, 0, 0);
          const b = await createImageBitmap(c);
          finish(b);
        } catch { finish(null); }
      };
      v.onerror = () => finish(null);
      v.src = URL.createObjectURL(file);
      setTimeout(() => finish(null), 5000); // iOS Brave/Safari often never fires 'seeked' for in-app recordings
    } catch { resolve(null); }
  });
}

// Tiny (~24px) blurred placeholder so the feed paints instantly before the full
// encrypted media downloads/decrypts. Returns raw JPEG bytes (ArrayBuffer) or null.
async function makeThumbBytes(file, type) {
  try {
    const bitmap = type === "video" ? await videoFrameBitmap(file) : await createImageBitmap(file);
    if (!bitmap) return null;
    const max = 24;
    const longest = Math.max(bitmap.width, bitmap.height) || 1;
    const scale = Math.min(1, max / longest);
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.5));
    return blob ? await blob.arrayBuffer() : null;
  } catch { return null; }
}

export default function CreatePost({ open, onClose, onCreated, initialText }) {
  const { hasNetwork, encryptNetText, encryptNetBytes, decryptNetText } = useCrypto();
  const { user, refresh } = useAuth();
  const [slides, setSlides] = useState([]); // [{ file, type, preview }]
  const [caption, setCaption] = useState("");
  const [categories, setCategories] = useState([]);
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState(null); // { label, pct|null }
  const [error, setError] = useState("");
  const [designer, setDesigner] = useState(null); // { mode:'text'|'photo', slideIndex:number|null, imageUrl:string|null }
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [newCollectionTitle, setNewCollectionTitle] = useState("");
  const inputRef = useRef();
  useEffect(() => {
    if (!open || !initialText) return;
    setCaption(initialText);
    const t = setTimeout(() => document.querySelector('[data-testid="post-caption-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
    return () => clearTimeout(t);
  }, [open, initialText]);

  useEffect(() => {
    if (!open || !user?.id) return;
    api.get(`/collections?owner=${user.id}`).then(async ({ data }) => {
      const out = [];
      for (const c of data) {
        let t = c.title || "Untitled collection";
        if (c.enc && c.title_ct) { const d = await decryptNetText(c.title_ct, c.title_iv); t = d || "Untitled collection"; }
        out.push({ id: c.id, title: t });
      }
      setCollections(out);
    }).catch(() => {});
  }, [open, user, decryptNetText]);

  if (!open) return null;

  const reset = () => {
    slides.forEach((s) => URL.revokeObjectURL(s.preview));
    setSlides([]); setCaption(""); setCategories([]); setError(""); setDesigner(null);
    setCollectionId(""); setNewCollectionTitle("");
  };
  const close = () => { reset(); onClose(); };

  const openTextDesigner = () => setDesigner({ mode: "text", slideIndex: null, imageUrl: null });
  const openMediaDesigner = (i) => setDesigner({
    mode: "media", slideIndex: i, imageUrl: slides[i].preview, mediaType: slides[i].type,
    initialScrim: slides[i].overlay?.scrim, initialBoxes: slides[i].overlay?.boxes,
  });
  const onDesignerDone = (result) => {
    if (designer.mode === "text") {
      const file = result;
      const preview = URL.createObjectURL(file);
      setSlides((prev) => [...prev, { file, type: "image", preview }].slice(0, MAX_SLIDES));
    } else {
      const overlay = result && result.boxes && result.boxes.length ? result : null;
      setSlides((prev) => prev.map((s, idx) => idx === designer.slideIndex ? { ...s, overlay } : s));
    }
    setDesigner(null);
  };

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setError("");
    const valid = [];
    for (const f of picked) {
      const isVideo = isVideoFile(f);
      if (isVideo) {
        if (f.size > MAX_VIDEO_BYTES) {
          setError(`"${f.name}" is too large. Please keep videos under 500 MB (for longer clips, record at 1080p instead of 4K).`);
          continue;
        }
        const dur = await getVideoDuration(f);
        if (dur > MAX_VIDEO_SECONDS + 1) {
          setError(`"${f.name}" is ${Math.round(dur)}s. Please keep videos under 15 minutes for now.`);
          continue;
        }
      }
      valid.push({ file: f, type: isVideo ? "video" : "image", preview: URL.createObjectURL(f) });
    }
    if (valid.length) setSlides((prev) => [...prev, ...valid].slice(0, MAX_SLIDES));
    e.target.value = "";
  };

  const removeSlide = (i) => {
    setSlides((prev) => {
      URL.revokeObjectURL(prev[i].preview);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const uploadBlob = async (blob, filename, onPct, enc = false) => {
    // Small files: single request. Large (e.g. encrypted videos): chunked to
    // bypass the single-request ingress size limit.
    if (blob.size <= CHUNK_THRESHOLD) {
      const fd = new FormData();
      fd.append("file", blob, filename);
      fd.append("enc", enc ? "true" : "false");
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (onPct && e.total) onPct(Math.round((e.loaded / e.total) * 100)); },
      });
      return data.path;
    }
    const uploadId = (window.crypto?.randomUUID?.() ) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const part = blob.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, blob.size));
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("chunk", part, "chunk");
      await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (onPct) onPct(Math.round(((i + 1) / totalChunks) * 100));
    }
    const { data } = await api.post("/upload/complete", {
      upload_id: uploadId, filename, content_type: blob.type || "application/octet-stream", enc,
    });
    return data.path;
  };

  // Encrypt + upload a (large) video in bounded memory: read one 4MB slice at a
  // time, encrypt it as its own AES-GCM frame, and upload immediately, so the
  // whole file never sits decrypted OR ciphered in memory (fixes mobile crashes
  // and lets us allow much longer videos). The framed format is self-describing
  // (MAGIC header + per-chunk IV), so playback auto-detects it, no metadata IV.
  const encryptedVideoUpload = async (file, onPct) => {
    const uploadId = (window.crypto?.randomUUID?.()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const size = file.size;
    let index = 0;
    for (let start = 0; start < size; start += CHUNK_SIZE) {
      const buf = await file.slice(start, Math.min(start + CHUNK_SIZE, size)).arrayBuffer();
      const { ctBuf, ivB64 } = await encryptNetBytes(buf);
      const header = new ArrayBuffer(4);
      new DataView(header).setUint32(0, ctBuf.byteLength);
      const iv = b642buf(ivB64); // 12-byte ArrayBuffer
      const parts = index === 0 ? [MEDIA_CHUNK_MAGIC, header, iv, ctBuf] : [header, iv, ctBuf];
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(index));
      fd.append("chunk", new Blob(parts), "chunk");
      await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      index++;
      if (onPct) onPct(Math.round((Math.min(start + CHUNK_SIZE, size) / size) * 100));
    }
    const { data } = await api.post("/upload/complete", {
      upload_id: uploadId, filename: "enc.bin", content_type: "application/octet-stream", enc: true,
    });
    return data.path;
  };

  const submit = async () => {
    if (!slides.length && !caption.trim()) { setError("Add a photo, a video, or some words to share."); return; }
    if (!categories.length) { setError("Choose at least one realm."); return; }
    setPosting(true); setError("");
    try {
      const enc = hasNetwork();
      const media = [];
      const total = slides.length;
      for (let i = 0; i < slides.length; i++) {
        const s = slides[i];
        const n = i + 1;
        let srcFile = s.file;
        if (s.type === "image") {
          setProgress({ label: `Compressing image ${n} of ${total}…`, pct: null });
          srcFile = await compressImage(s.file);
        }
        if (enc) {
          let path, ivB64 = null;
          if (s.type === "video") {
            setProgress({ label: `Encrypting & uploading video ${n} of ${total}…`, pct: 0 });
            path = await encryptedVideoUpload(srcFile, (pct) => setProgress({ label: `Encrypting & uploading video ${n} of ${total}…`, pct }));
          } else {
            setProgress({ label: `Encrypting ${s.type} ${n} of ${total}…`, pct: null });
            const buf = await srcFile.arrayBuffer();
            const encd = await encryptNetBytes(buf);
            ivB64 = encd.ivB64;
            path = await uploadBlob(new Blob([encd.ctBuf]), "enc.bin", (pct) => setProgress({ label: `Uploading ${n} of ${total}…`, pct }), true);
          }
          // Encrypted blur-up thumbnail (best-effort; must NEVER fail the post).
          setProgress({ label: `Finalizing ${n} of ${total}…`, pct: null });
          let thumb = null, thumb_iv = null;
          try {
            const tb = await makeThumbBytes(s.file, s.type);
            if (tb) {
              const et = await encryptNetBytes(tb);
              if (et && et.ctBuf) { thumb = buf2b64(et.ctBuf); thumb_iv = et.ivB64; }
            }
          } catch { /* thumbnail is optional, ignore */ }
          const item = { path, type: s.type, iv: ivB64, thumb, thumb_iv };
          if (s.overlay && s.overlay.boxes && s.overlay.boxes.length) {
            const ov = await encryptNetText(JSON.stringify(s.overlay));
            if (ov.enc) { item.overlay_ct = ov.ct; item.overlay_iv = ov.iv; }
            else item.overlay = s.overlay;
          }
          media.push(item);
        } else {
          const path = await uploadBlob(srcFile, s.file.name, (pct) => setProgress({ label: `Uploading ${n} of ${total}…`, pct }));
          const item = { path, type: s.type, iv: null };
          if (s.overlay && s.overlay.boxes && s.overlay.boxes.length) item.overlay = s.overlay;
          media.push(item);
        }
      }
      setProgress({ label: "Publishing your signal…", pct: null });
      // Publish the final record with one silent auto-resend: if the connection
      // drops during a brief cold-start, wait for the server to return and retry
      // once (media is already uploaded, so this only re-sends the small record).
      const sendOnce = async (fn) => {
        try { return await fn(); }
        catch (e) {
          if (!e?.response || e?.code === "ECONNABORTED") {
            setProgress({ label: "Connection dropped, reconnecting and sending automatically…", pct: null });
            const back = await waitForServerUp(60000);
            if (!back) throw e;
            return await fn();
          }
          throw e;
        }
      };
      const first = media[0] || null;
      let body = { category: categories[0], categories, media, enc };
      if (first) { body = { ...body, media_path: first.path, media_type: first.type, media_iv: first.iv }; }
      if (enc) {
        const cap = await encryptNetText(caption);
        body = { ...body, caption_ct: cap.ct, caption_iv: cap.iv, caption: "" };
      } else {
        body = { ...body, caption };
      }
      let finalCollectionId = collectionId && collectionId !== "__new__" ? collectionId : null;
      if (collectionId === "__new__" && newCollectionTitle.trim()) {
        let cp = { title: newCollectionTitle.trim(), enc: false };
        if (enc) { const ce = await encryptNetText(newCollectionTitle.trim()); cp = { title_ct: ce.ct, title_iv: ce.iv, enc: true, title: "" }; }
        const { data: nc } = await sendOnce(() => api.post("/collections", cp, { timeout: 45000 }));
        finalCollectionId = nc.id;
      }
      if (finalCollectionId) body.collection_id = finalCollectionId;
      await sendOnce(() => api.post("/posts", body, { timeout: 45000 }));
      reset(); onCreated && onCreated(); onClose();
      if (refresh) refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (detail) msg = formatApiErrorDetail(detail);
      else if (e.response) msg = `Could not transmit (server error ${e.response.status}). Please try again.`;
      else if (e?.code === "ECONNABORTED") msg = "The server took too long to respond, so your signal wasn't sent. Please check your connection and try again.";
      else if (e?.message === "Network Error" || !e?.response) msg = "Couldn't reach the server, so your signal wasn't sent. Please check your connection and try again.";
      else if (e?.message) msg = `Could not transmit: ${e.message}`;
      else msg = "Could not transmit. Please try again.";
      console.error("Transmit failed:", e);
      setError(msg);
    }
    finally { setPosting(false); setProgress(null); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="create-post-modal">
      {designer && (
        <PostDesigner
          mode={designer.mode}
          imageUrl={designer.imageUrl}
          mediaType={designer.mediaType}
          initialScrim={designer.initialScrim}
          initialBoxes={designer.initialBoxes}
          onDone={onDesignerDone}
          onCancel={() => setDesigner(null)}
        />
      )}
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-heading text-lg text-white">Transmit a Signal</h3>
          <button onClick={close} data-testid="close-compose-button" className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          {!user?.has_posted && (
            <div className="border border-[#14A89A]/30 bg-[#14A89A]/[0.06] px-4 py-3" data-testid="first-post-banner">
              <p className="font-body text-sm text-zinc-200 leading-relaxed">
                <span className="text-[#14A89A] font-medium">New here?</span> Introduce yourself, what you're building, creating or exploring, and what you're hoping to find here. It's how the community meets you.
              </p>
            </div>
          )}
          <CoachTips ids={slides.some((s) => s.type === "image") ? ["first-post", "design-tool"] : ["first-post"]} />
          {slides.length === 0 ? (
            <div className="space-y-3">
              <button onClick={() => inputRef.current.click()} data-testid="media-dropzone" className="w-full border border-dashed border-white/20 hover:border-[#14A89A] transition-colors py-14 flex flex-col items-center gap-3 text-zinc-400">
                <UploadCloud strokeWidth={1.5} />
                <span className="font-body text-sm">Upload images or videos, add multiple for a slideshow</span>
                <span className="font-body text-[10px] text-zinc-500">Up to 10 slides · videos ≤ 15 min &amp; 500 MB</span>
                <span className="font-body text-[10px] text-zinc-600">Longer clip? Record at 1080p (not 4K) for a faster upload</span>
                <span className="font-body text-[10px] text-[#14A89A]/90">You can overlay text on each photo/video after uploading</span>
                {hasNetwork() && <span className="font-body text-[10px] flex items-center gap-1 text-[#14A89A]"><Lock size={10} /> End-to-end encrypted</span>}
              </button>
              <div className="flex items-center gap-3 text-zinc-600">
                <div className="h-px flex-1 bg-white/10" /><span className="font-body text-[10px] uppercase tracking-widest">or</span><div className="h-px flex-1 bg-white/10" />
              </div>
              <button onClick={openTextDesigner} data-testid="create-text-post-button" className="w-full border border-white/15 hover:border-[#D4AF37] transition-colors py-4 flex flex-col items-center justify-center gap-1 text-zinc-300 hover:text-white font-body text-sm">
                <span className="flex items-center gap-2"><Type size={16} /> Create a text-only post</span>
                <span className="font-body text-[10px] text-zinc-500">Just words on a color or gradient background, no photo or video</span>
              </button>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-3 gap-2" data-testid="slide-previews">
                {slides.map((s, i) => (
                  <div key={i} className="relative aspect-square border border-white/10 overflow-hidden bg-black">
                    {s.type === "video"
                      ? <video src={s.preview} className="w-full h-full object-cover" />
                      : <img src={s.preview} alt="" className="w-full h-full object-cover" />}
                    <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 rounded-full">{i + 1}</span>
                    <button onClick={() => removeSlide(i)} data-testid={`remove-slide-${i}`} title="Remove" className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-red-600 transition-colors"><X size={12} /></button>
                    <button onClick={() => openMediaDesigner(i)} data-testid={`add-text-slide-${i}`} title={s.overlay ? "Edit text overlay" : "Add text over this media"} className={`absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 py-1.5 font-body text-[10px] font-medium transition-colors ${s.overlay ? "bg-[#14A89A] text-[#0A0A0A]" : "bg-black/75 text-white hover:bg-[#14A89A] hover:text-[#0A0A0A]"}`}>
                      <Type size={12} /> {s.overlay ? "Text added" : "Add text"}
                    </button>
                  </div>
                ))}
                {slides.length < MAX_SLIDES && (
                  <button onClick={() => inputRef.current.click()} data-testid="add-slide-button" className="aspect-square border border-dashed border-white/20 hover:border-[#14A89A] transition-colors flex flex-col items-center justify-center gap-1 text-zinc-400">
                    <Plus size={20} />
                    <span className="font-body text-[10px]">Add slide</span>
                  </button>
                )}
                {slides.length < MAX_SLIDES && (
                  <button onClick={openTextDesigner} data-testid="add-text-card-button" className="aspect-square border border-dashed border-white/20 hover:border-[#D4AF37] transition-colors flex flex-col items-center justify-center gap-1 text-zinc-400 hover:text-white">
                    <Type size={20} />
                    <span className="font-body text-[10px]">Add text card</span>
                  </button>
                )}
              </div>
              <p className="mt-2 font-body text-[11px] text-zinc-500">{slides.length}/{MAX_SLIDES} slide{slides.length > 1 ? "s" : ""}{hasNetwork() ? " · end-to-end encrypted" : ""}</p>
              <p className="mt-1 font-body text-[11px] text-zinc-500 flex items-center gap-1.5"><Type size={12} className="text-[#14A89A]" /> Tap <span className="text-zinc-300">Add text</span> on any photo or video to overlay words on top (up to 2 blocks).</p>
              {slides.some((s) => s.type === "video") && (
                <p className="mt-1 font-body text-[10px] text-zinc-600" data-testid="video-1080-tip">Tip: for a faster upload on long videos, record at 1080p rather than 4K.</p>
              )}
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/*,video/*" multiple onChange={handleFiles} className="hidden" data-testid="media-file-input" />

          <div>
            <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Realms: choose up to 3</span>
            <div className="mt-3" data-testid="post-realm-picker">
              <DomainPicker value={categories} onChange={setCategories} max={3} />
            </div>
            {categories.length > 0 && CATEGORY_DESC[categories[0]] && (
              <p className="mt-2 font-body text-xs text-zinc-500 leading-snug" data-testid="post-category-desc">{CATEGORY_DESC[categories[0]]}</p>
            )}
          </div>

          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} placeholder="Share your signal in words, a note, a story, a question. Photos and video are optional." data-testid="post-caption-input" className="bg-[#0A0A0A] border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] w-full font-body resize-none" />
          <p className="-mt-4 font-body text-[11px] text-zinc-500">You can post with just words, a photo or video is optional.</p>

          <div>
            <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Add to a collection <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></span>
            <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)} data-testid="post-collection-select" className="mt-3 bg-[#0A0A0A] border border-white/15 px-3 py-3 text-white w-full font-body focus:outline-none focus:border-[#14A89A] text-sm">
              <option value="">None, just a regular post</option>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              <option value="__new__">＋ New collection…</option>
            </select>
            {collectionId === "__new__" && (
              <input value={newCollectionTitle} onChange={(e) => setNewCollectionTitle(e.target.value)} placeholder="Name your collection (e.g. 30-Day Garden Series)" data-testid="new-collection-title-input" className="mt-2 bg-[#0A0A0A] border border-white/15 px-3 py-3 text-white placeholder:text-zinc-600 w-full font-body focus:outline-none focus:border-[#14A89A] text-sm" />
            )}
            {collectionId && collectionId !== "__new__" && <p className="mt-2 font-body text-[11px] text-zinc-500">This post will still appear normally in your feed, it just also joins this collection on your profile.</p>}
          </div>

          {error && <p className="text-red-400 text-sm font-body" data-testid="compose-error">{error}</p>}

          {progress && (
            <div className="space-y-2" data-testid="upload-progress">
              <div className="flex items-center justify-between font-body text-xs text-zinc-400">
                <span className="flex items-center gap-1.5"><SignalPulse size={13} inline /> {progress.label}</span>
                {progress.pct !== null && <span className="text-[#14A89A]">{progress.pct}%</span>}
              </div>
              <div className="h-1 w-full bg-white/10 overflow-hidden">
                {progress.pct !== null
                  ? <div className="h-full bg-[#14A89A] transition-all duration-200" style={{ width: `${progress.pct}%` }} />
                  : <div className="h-full w-1/3 bg-[#14A89A] upload-indeterminate" />}
              </div>
            </div>
          )}

          <button onClick={submit} disabled={posting} data-testid="submit-post-button" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
            {posting ? <><SignalPulse size={16} inline /> Transmitting…</> : "Transmit"}
          </button>
        </div>
      </div>
    </div>
  );
}
